import { ThaiLottoCardProps } from './ThaiLottoCard.types'
import { useTranslation } from 'react-i18next'
import { useThemeAssets } from '../../hooks/useThemeAssets'
import LottoCardFlagHeader from '../LottoCardFlagHeader'
import LottoResultsDivider from '../LottoResultsDivider'
import { partitionThaiLottoGroups } from './sectionUtils'
import styles from './ThaiLottoCard.module.css'

const ThaiLottoCard: React.FC<ThaiLottoCardProps> = ({
  country,
  flagBackgroundName,
  title,
  date,
  time,
  sections,
  noRound = false,
  onClick,
}) => {
  const { t } = useTranslation()
  const { smallFlagImageUrls, backgroundFlagImageUrls } = useThemeAssets()

  return (
    <div
      className={`thai-lotto-card-bg ${styles.card} ${noRound ? styles.noRound : ''}`}
      style={{
        backgroundImage: `url(${backgroundFlagImageUrls[flagBackgroundName]})`,
      }}
      onClick={onClick}
    >
      <div className={styles.cardInner}>
        <div
          className={`${
            noRound ? 'disabled-gradient' : 'thai-lotto-card-content'
          } ${styles.content} sm:pt-4 pt-3 sm:pb-8 pb-4 sm:px-8 px-4`}
        >
          {/* Header */}
          <LottoCardFlagHeader
            title={title}
            country={country}
            imageMap={smallFlagImageUrls}
            variant="thai"
          />

          {/* Date and Time */}
          <div className="flex justify-between text-sm mb-0 md:mb-2 text-[var(--lotto-card-date-text)]">
            <span>{date}</span>
            <span>{time}</span>
          </div>

          {/* Divider */}
          <LottoResultsDivider label={t('lottoCard.results')} variant="thai" />

          {/* Scrollable Section */}
          <div className="md:overflow-y-auto md:max-h-[230px] pr-2 md:legacy-scrollbar">
            <div className="flex flex-wrap md:gap-4 items-center justify-center overflow-hidden">
              {sections.map((section, sectionIndex) => {
                const orderedGroups = partitionThaiLottoGroups(section.numbers)
                return (
                  <div
                    key={sectionIndex}
                    className="flex flex-col md:border md:border-black/30 md:rounded-2xl md:bg-white/5 items-center text-center px-2 sm:px-6 md:px-12 md:py-2 min-w-0 flex-shrink-0 mb-2"
                  >
                    <h5 className="font-semibold mb-0 text-xs sm:text-sm text-[var(--lotto-card-section-title)]">
                      {section.title}
                    </h5>
                    <div className="flex flex-wrap justify-center gap-1 sm:gap-2 overflow-hidden">
                      {orderedGroups.map(
                        ({ group, originalIndex }, groupIdx) => {
                          if (typeof group === 'string') {
                            return (
                              <span
                                key={groupIdx}
                                className="text-xs text-[var(--card-subtext)] md:text-lg font-normal"
                              >
                                {group}
                              </span>
                            )
                          }

                          const isGrouped = Array.isArray(group)
                          return (
                            <h3
                              key={groupIdx}
                              className={`flex gap-1 sm:gap-2 ${
                                isGrouped && originalIndex > 0
                                  ? 'ml-2 sm:ml-3 md:ml-4'
                                  : ''
                              } flex-shrink-0`}
                            >
                              {isGrouped ? (
                                group.map((num, idx) => (
                                  <div
                                    key={idx}
                                    className="result-number-style border thai-lotto-number"
                                  >
                                    {num}
                                  </div>
                                ))
                              ) : (
                                <div className="result-number-style border thai-lotto-number">
                                  {group}
                                </div>
                              )}
                            </h3>
                          )
                        },
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ThaiLottoCard
